import React from 'react';
import axios from 'axios';
import Button from '../../components/Button';

// Session / Sign-out policy — how long an account stays signed in before it must
// re-authenticate. Refresh tokens rotate on use, so this is an IDLE window: an
// account stays signed in while active and re-auths only after being away the
// whole window. Role-aware (admins short, read-only/other users long). Talks to
// /api/session-settings directly. Provider-only on the backend; renders nothing
// otherwise.

const label = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' };
const inputCss = {
	padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box',
	background: 'var(--input-bg, var(--card-bg, rgba(0,0,0,.15)))', color: 'var(--text-primary)',
	border: '1px solid var(--border-color)', borderRadius: 8,
};

const SessionSettings = ({ styles }) => {
	const [form, setForm] = React.useState(null);
	const [bounds, setBounds] = React.useState({ min: 1, max: 365 });
	const [loading, setLoading] = React.useState(true);
	const [busy, setBusy] = React.useState(false);
	const [toast, setToast] = React.useState({ type: '', message: '' });

	const load = React.useCallback(async () => {
		try {
			const { data } = await axios.get('/api/session-settings');
			if (data && data.status) {
				setForm({
					SessionDaysAdmin: String(data.SessionDaysAdmin ?? '1'),
					SessionDaysUser: String(data.SessionDaysUser ?? '90'),
				});
				setBounds({ min: data.min ?? 1, max: data.max ?? 365 });
			}
		} catch (e) { /* not a provider, or request failed — render nothing */ }
		setLoading(false);
	}, []);

	React.useEffect(() => { load(); }, [load]);

	const setStr = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

	const save = async () => {
		setBusy(true);
		setToast({ type: '', message: '' });
		try {
			const { data } = await axios.post('/api/session-settings', { ...form },
				{ headers: { 'Content-Type': 'application/json' } });
			if (data && data.status) {
				setToast({ type: 'success', message: 'Session policy saved — applies to new sign-ins.' });
				setForm({ SessionDaysAdmin: String(data.SessionDaysAdmin), SessionDaysUser: String(data.SessionDaysUser) });
			} else {
				setToast({ type: 'error', message: (data && data.msg) || 'Request failed' });
			}
		} catch (e) {
			setToast({ type: 'error', message: e?.response?.data?.msg || e?.message || 'Request failed' });
		}
		setBusy(false);
	};

	if (loading || !form) return null;

	return (
		<div style={{ marginTop: 28, borderTop: '1px solid var(--border-color)', paddingTop: 20, maxWidth: 760 }}>
			<h2 style={{ marginBottom: 4 }}>Session &amp; Sign-out</h2>
			<p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 0 }}>
				How long an account stays signed in before it must re-authenticate. This is an
				<strong> idle timeout</strong> — an account stays signed in while it keeps using the portal,
				and is only signed out after being away for the whole window. Admins get a short window;
				read-only and other users a longer one.
			</p>

			<div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginTop: 8 }}>
				<label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 220px' }}>
					<span style={label}>Admin session (days)</span>
					<input type='number' min={bounds.min} max={bounds.max} style={inputCss}
						value={form.SessionDaysAdmin} onChange={setStr('SessionDaysAdmin')} />
				</label>
				<label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 220px' }}>
					<span style={label}>User / read-only session (days)</span>
					<input type='number' min={bounds.min} max={bounds.max} style={inputCss}
						value={form.SessionDaysUser} onChange={setStr('SessionDaysUser')} />
				</label>
			</div>
			<p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
				Allowed range {bounds.min}–{bounds.max} days. Changes apply to new sign-ins (and to active
				sessions on their next token refresh); they don't retroactively extend sessions already signed in.
			</p>

			<div style={{ marginTop: 12 }}>
				<Button onClick={save} disabled={busy}>Save</Button>
			</div>

			{toast.message && (
				<div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8,
					background: toast.type === 'error' ? 'rgba(192,20,18,.1)' : 'rgba(18,192,119,.12)',
					border: `1px solid ${toast.type === 'error' ? 'rgba(192,20,18,.35)' : 'rgba(18,192,119,.4)'}`,
					color: 'var(--text-primary)' }}>
					{toast.message}
				</div>
			)}
		</div>
	);
};

export default SessionSettings;
